import { supabase } from './supabase';
import { validatePassword } from './auth.validation';

// Rate limiting
const RATE_LIMIT_WINDOW = 3600000; // 1 hour
const MAX_ATTEMPTS = 5;
const attempts = new Map<string, { count: number; timestamp: number }>();

function checkRateLimit(identifier: string): boolean {
  const now = Date.now();
  const attempt = attempts.get(identifier);

  if (!attempt) {
    attempts.set(identifier, { count: 1, timestamp: now });
    return true;
  }

  if (now - attempt.timestamp > RATE_LIMIT_WINDOW) {
    attempts.set(identifier, { count: 1, timestamp: now });
    return true;
  }

  if (attempt.count >= MAX_ATTEMPTS) {
    return false;
  }

  attempt.count++;
  return true;
}

export async function signIn(email: string, password: string) {
  const identifier = `${email}-${window.location.hostname}`;

  try {
    if (!checkRateLimit(identifier)) {
      throw new Error('Too many login attempts. Please try again later.');
    }

    console.log('Signing in with email:', email);
    
    // Use password authentication instead of OTP
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Supabase auth error:', error);
      throw error;
    }

    if (!data?.user) {
      throw new Error('No user data returned');
    }

    console.log('Sign in successful, user:', data.user.id);

    // Log successful attempt
    try {
      await supabase.from('login_attempts').insert({
        email,
        ip_address: 'client',
        success: true
      });
    } catch (logError) {
      console.error('Failed to log login attempt:', logError);
    }

    // Clear rate limit on successful login
    attempts.delete(identifier);

    return { data };
  } catch (error) {
    console.error('Sign in error:', error);

    // Log failed attempt
    try {
      await supabase.from('login_attempts').insert({
        email,
        ip_address: 'client',
        success: false,
        failure_reason: error instanceof Error ? error.message : 'Unknown error'
      });
    } catch (logError) {
      console.error('Failed to log login attempt:', logError);
    }

    throw error;
  }
}

export async function signUp(email: string, password: string, fullName: string) {
  try {
    // Validate password strength
    const { isValid, feedback } = validatePassword(password);
    if (!isValid) {
      throw new Error(feedback[0] || 'Password is too weak');
    }

    console.log('Signing up with email:', email);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) {
      console.error('Supabase auth error:', error);
      throw error;
    }

    if (!data?.user) {
      throw new Error('No user data returned');
    }

    console.log('Sign up successful, user:', data.user.id);

    // Create user profile
    try {
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          id: data.user.id,
          email: data.user.email || email,
          full_name: fullName
        });

      if (profileError) {
        console.error('Error creating user profile:', profileError);
        // Continue anyway since the auth user was created
      }
    } catch (profileError) {
      console.error('Error creating user profile:', profileError);
      // Continue anyway since the auth user was created
    }

    return { data };
  } catch (error) {
    console.error('Sign up error:', error);
    throw error;
  }
}

export async function signOut() {
  try {
    console.log('Signing out');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // Clear any session cookies
    document.cookie = 'advisorgrow-auth=; Max-Age=0; path=/; domain=' + window.location.hostname;
    
    // Clear local storage
    // localStorage.removeItem('advisorgrow.auth.token'); // Removed since supabase.auth.signOut() clears all items from localStorage
    // localStorage.removeItem('supabase.auth.token'); // Removed since supabase.auth.signOut() clears all items from localStorage
    
    console.log('Sign out successful');
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

let lastMagicLinkRequest = 0;
const MAGIC_LINK_COOLDOWN = 30000; // 30 seconds cooldown

export async function requestMagicLink(email: string) {
  const identifier = `${email}-${window.location.hostname}`;

  try {
    if (!checkRateLimit(identifier)) {
      throw new Error('Too many attempts. Please try again later.');
    }

    // Check cooldown period
    const now = Date.now();
    const timeSinceLastRequest = now - lastMagicLinkRequest;
    
    if (timeSinceLastRequest < MAGIC_LINK_COOLDOWN) {
      const remainingTime = Math.ceil((MAGIC_LINK_COOLDOWN - timeSinceLastRequest) / 1000);
      throw new Error(`Please wait ${remainingTime} seconds before requesting another link`);
    }

    console.log('Requesting magic link for:', email);

    // Use password reset instead of OTP since OTP is disabled
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    if (error) {
      console.error('Magic link error:', error);
      throw error;
    }
    
    // Update last request timestamp
    lastMagicLinkRequest = now;
    
    // Log the attempt
    try {
      await supabase.from('login_attempts').insert({
        email,
        ip_address: 'client',
        success: true
      });
    } catch (logError) {
      console.error('Failed to log login attempt:', logError);
    }

    console.log('Magic link sent successfully');
    return { success: true };
  } catch (error) {
    console.error('Magic link error:', error);
    
    // Log failed attempt
    try {
      await supabase.from('login_attempts').insert({
        email,
        ip_address: 'client',
        success: false,
        failure_reason: error instanceof Error ? error.message : 'Unknown error'
      });
    } catch (logError) {
      console.error('Failed to log login attempt:', logError);
    }
    
    throw error;
  }
}

export async function signInWithGoogle() {
  try {
    console.log('Signing in with Google');
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });

    if (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
    
    console.log('Google sign in initiated, data:', data);
    return { success: true, data };
  } catch (error) {
    console.error('Google sign in error:', error);
    throw error;
  }
}

// Password reset functions
export async function requestPasswordReset(email: string) {
  try {
    console.log('Requesting password reset for:', email);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });

    if (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
    
    console.log('Password reset email sent');
    return { success: true };
  } catch (error) {
    console.error('Password reset request error:', error);
    throw error;
  }
}

export async function updatePassword(newPassword: string) {
  try {
    // Validate password strength
    const { isValid, feedback } = validatePassword(newPassword);
    if (!isValid) {
      throw new Error(feedback[0] || 'Password is too weak');
    }

    console.log('Updating password');
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error('Password update error:', error);
      throw error;
    }
    
    console.log('Password updated successfully');
    return { success: true };
  } catch (error) {
    console.error('Password update error:', error);
    throw error;
  }
}

export async function verifyMagicLink() {
  try {
    console.log('Verifying magic link');
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Magic link verification error:', error);
      throw error;
    }
    
    console.log('Magic link verification result:', data.session ? 'Success' : 'No session');
    return { session: data.session };
  } catch (error) {
    console.error('Magic link verification error:', error);
    throw error;
  }
}